import React, { Component, Fragment } from 'react';
import * as THREE from 'three';
import CANNON from 'cannon';
import {
  Scene,
  Group,
  SpotLight,
  AmbientLight,
  BoxGeometry
} from './components/three-components';
import { side1, side2, side3, side4, side5, side6 } from './images';

class DiceScene extends Component {
  constructor() {
    super();

    const diceSides = [side1, side2, side3, side4, side5, side6];
    const diceMaterials = diceSides.map(side => {
      const texture = new THREE.TextureLoader().load(side);
      return new THREE.MeshBasicMaterial({ map: texture });
    });

    this.state = {
      diceMaterials,
      x: 0,
      y: 0,
      moveCounter: 0
    };

    this.mainScene = null;
    this.initCannon = this.initCannon.bind(this);
    this.onAnimate = this.onAnimate.bind(this);
    this.shuffle = this.shuffle.bind(this);
    this.handleMotion = this.handleMotion.bind(this);
  }

  handleMotion(event) {
    let { x, y, z } = event.accelerationIncludingGravity;
    const isIos = navigator.userAgent.includes('Mac');

    if (isIos) {
      x = -x;
      y = -y;
    }

    const prevX = this.state.x;
    const prevY = this.state.y;
    const prevZ = this.state.z;

    const prevMoveCounter = this.state.moveCounter;

    if (Math.abs(x) >= 1 && Math.abs(y) >= 1 && Math.abs(z) >= 1) {
      const diffX = x - prevX;
      const diffY = y - prevY;
      const diffZ = z - prevZ;

      const deltaX = Math.abs(diffX);
      const deltaY = Math.abs(diffY);
      const deltaZ = Math.abs(diffZ);

      const moveCounter =
        deltaX + deltaY + deltaZ > 3
          ? prevMoveCounter + 1
          : Math.max(0, prevMoveCounter - 1);
      const isShaking = moveCounter > 6;

      if (isShaking) {
        this.shuffle({
          x: Math.min(Math.max(-(x * 10), -130), 130),
          y: deltaZ * 5,
          z: y * 3
        });
      }

      this.setState({
        x,
        y,
        z,
        moveCounter: isShaking ? 0 : moveCounter
      });
    }
  }

  shuffle(data = {}) {
    const { x, y, z } = data;
    let dice = [this.diceABody, this.diceBBody];

    dice.forEach(({ quaternion, velocity, angularVelocity }) => {
      quaternion.x = ((Math.random() * 90 - 45) * Math.PI) / 180;
      quaternion.z = ((Math.random() * 90 - 45) * Math.PI) / 180;

      velocity.set(x, y, z);

      angularVelocity.set(
        20 * Math.random() - 10,
        20 * Math.random() - 10,
        20 * Math.random() - 10
      );
    });
  }

  initCannon() {
    const earthGravity = 9.807;
    this.world = new CANNON.World();
    this.world.gravity.set(0, -(earthGravity * 20), 0);
    this.world.broadphase = new CANNON.NaiveBroadphase();
    this.world.solver.iterations = 16;

    this.diceBodyMaterial = new CANNON.Material();
    this.floorBodyMaterial = new CANNON.Material();
    this.barrierBodyMaterial = new CANNON.Material();

    this.world.addContactMaterial(
      new CANNON.ContactMaterial(
        this.floorBodyMaterial,
        this.diceBodyMaterial,
        { friction: 0.5, restitution: 0.8 }
      )
    );

    this.world.addContactMaterial(
      new CANNON.ContactMaterial(
        this.barrierBodyMaterial,
        this.diceBodyMaterial,
        { friction: 0, restitution: 1.0 }
      )
    );

    this.world.addContactMaterial(
      new CANNON.ContactMaterial(this.diceBodyMaterial, this.diceBodyMaterial, {
        friction: 0,
        restitution: 0.5
      })
    );

    const diceBody = {
      mass: 0.5,
      shape: new CANNON.Box(new CANNON.Vec3(1, 1, 1)),
      material: this.diceBodyMaterial,
      angularVelocity: new CANNON.Vec3(0, 10, 0),
      angularDamping: 0.5
    };

    this.barriersBody = new CANNON.Body({
      mass: 1000,
      position: new CANNON.Vec3(0, 20, 0),
      material: this.barrierBodyMaterial,
      angularDamping: 0.5
    });

    const horizontalWallShape = new CANNON.Box(new CANNON.Vec3(100, 11, 10));
    const verticalWallShape = new CANNON.Box(new CANNON.Vec3(10, 11, 11.5));
    const roofShape = new CANNON.Box(new CANNON.Vec3(100, 10, 100));

    const { innerWidth, innerHeight } = window;
    const verticalWallPositionX =
      (innerWidth / (innerHeight + innerWidth)) * 31.5 + 5;

    this.barriersBody.addShape(
      horizontalWallShape,
      new CANNON.Vec3(0, 0, -21.5),
      new CANNON.Quaternion()
    );
    this.barriersBody.addShape(
      horizontalWallShape,
      new CANNON.Vec3(0, 0, 21.5),
      new CANNON.Quaternion()
    );
    this.barriersBody.addShape(
      verticalWallShape,
      new CANNON.Vec3(verticalWallPositionX, 0, 0),
      new CANNON.Quaternion()
    );
    this.barriersBody.addShape(
      verticalWallShape,
      new CANNON.Vec3(-verticalWallPositionX, 0, 0),
      new CANNON.Quaternion()
    );
    this.barriersBody.addShape(
      roofShape,
      new CANNON.Vec3(0, 10, 0),
      new CANNON.Quaternion()
    );

    this.diceABody = new CANNON.Body(diceBody);
    this.diceABody.position.set(Math.random() * 2, 0, 0);

    this.diceBBody = new CANNON.Body(diceBody);
    this.diceBBody.position.set(Math.random() * 2, 0, 0);

    this.floorBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Plane(),
      material: this.floorBodyMaterial
    });
    this.floorBody.quaternion.setFromAxisAngle(
      new CANNON.Vec3(1, 0, 0),
      -Math.PI / 2
    );

    this.world.add(this.floorBody);
    this.world.add(this.barriersBody);

    setTimeout(() => {
      this.world.add(this.diceABody);
      this.world.add(this.diceBBody);
      this.setState({ addDiceToscene: true });
    }, 3000);
  }

  onAnimate() {
    if (!(this.mainScene && this.world)) {
      return;
    }

    const timeStep = 1 / 60;
    this.world.step(timeStep);

    const scene = this.mainScene.getScene();
    let diceA = scene.getObjectByName('box-a');
    let diceB = scene.getObjectByName('box-b');

    if (!(diceA && diceB)) {
      return;
    }

    diceA.position.copy(this.diceABody.position);
    diceA.quaternion.copy(this.diceABody.quaternion);

    diceB.position.copy(this.diceBBody.position);
    diceB.quaternion.copy(this.diceBBody.quaternion);
  }

  componentDidMount() {
    this.initCannon();
    window.addEventListener('devicemotion', this.handleMotion, true);
  }

  render() {
    const { diceMaterials, addDiceToscene } = this.state;

    return (
      <Scene
        ref={e => (this.mainScene = e)}
        fullScreen={true}
        background="#ffffff"
        onAnimate={this.onAnimate}
      >
        {scene => {
          return (
            <Fragment>
              <SpotLight scene={scene} />
              <AmbientLight scene={scene} />
              <Group
                name="dice-group"
                scene={scene}
                scale={0.4}
                positionZ={-7}
                rotationX={Math.PI / 2}
              >
                {group => {
                  return (
                    <Fragment>
                      <BoxGeometry
                        group={group}
                        color="#f7f7f7"
                        width={100}
                        height={100}
                        depth={1}
                        rotationX={Math.PI / 2}
                      />
                      {addDiceToscene && (
                        <Fragment>
                          <BoxGeometry
                            group={group}
                            name="box-a"
                            width={2}
                            height={2}
                            depth={2}
                            materials={diceMaterials}
                          />
                          <BoxGeometry
                            group={group}
                            name="box-b"
                            width={2}
                            height={2}
                            depth={2}
                            materials={diceMaterials}
                          />
                        </Fragment>
                      )}
                    </Fragment>
                  );
                }}
              </Group>
            </Fragment>
          );
        }}
      </Scene>
    );
  }
}

export default DiceScene;



// WEBPACK FOOTER //
// ./src/DiceScene.js